import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader, LoadingState, ErrorState } from "@/design-system";
import { api, formatApiError } from "@/lib/api";
import { useOffline } from "@/offline/OfflineProvider";

/**
 * Read-only searchable list. Online: fetches from the server and saves the
 * result on the device. Offline: shows the last saved copy with a notice.
 * Search is done locally over the loaded rows.
 */
export default function ListPage({ cacheKey, title, path, params, columns, rowKey = "id", unwrap, testid }) {
  const offline = useOffline();
  const store = offline?.store;
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(path, { params });
      const data = unwrap ? unwrap(r.data) : (Array.isArray(r.data) ? r.data : r.data.items || []);
      setRows(data);
      setFromCache(null);
      setError(null);
      if (store) await store.put("cache", `list:${cacheKey}`, { rows: data, savedAt: new Date().toISOString() });
    } catch (e) {
      const saved = store ? await store.get("cache", `list:${cacheKey}`) : null;
      if (saved && !e?.response) {
        setRows(saved.rows);
        setFromCache(saved.savedAt);
        setError(null);
      } else {
        setError(formatApiError(e));
      }
    }
  }, [path, params, cacheKey, store, unwrap]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!rows || !needle) return rows;
    return rows.filter((r) => columns.some((c) => String(c.value(r) ?? "").toLowerCase().includes(needle)));
  }, [rows, q, columns]);

  if (error && rows === null) return <ErrorState message={error} retry={load} testid={`${testid}-error`} />;
  if (rows === null) return <LoadingState testid={`${testid}-loading`} />;

  return (
    <div data-testid={testid}>
      <PageHeader title={title} />
      {fromCache && (
        <p className="text-sm text-amber-700 mb-2" data-testid="list-cached">
          Offline — showing the copy saved {fromCache.slice(0, 16).replace("T", " ")}.
        </p>
      )}
      <input aria-label="search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
        className="border p-2 mb-3 w-full max-w-sm text-sm" />
      <div className="overflow-x-auto border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>{columns.map((c) => <th key={c.label} className="p-2">{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r[rowKey]} className="border-t" data-testid={`row-${r[rowKey]}`}>
                {columns.map((c) => <td key={c.label} className="p-2">{c.value(r)}</td>)}
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td className="p-3 text-slate-500" colSpan={columns.length} data-testid="list-empty">Nothing found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
